import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import Icon from '../Icons';
import './styles.css';

const FeatureExplorer = ({ currentMember, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  const features = [
    {
      id: 'upload-memory',
      title: 'Upload Memories',
      description: 'Preserve photos, videos, documents, and stories',
      icon: 'photo',
      steps: [
        { text: 'Click "Upload Memory" card on dashboard', action: () => navigate(`/upload-memory/${currentMember._id}`) },
        { text: 'Select files from your device', action: null },
        { text: 'Add descriptions and tags', action: null },
        { text: 'Choose privacy settings', action: null },
        { text: 'Save to your vault', action: null }
      ],
      color: '#e2c89c'
    },
    {
      id: 'family-tree',
      title: 'Family Tree',
      description: 'Visualize your family connections and relationships',
      icon: 'family-tree',
      steps: [
        { text: 'Click "Family Tree" in navigation', action: () => navigate('/family-tree') },
        { text: 'View your family structure', action: null },
        { text: 'Add new family members', action: null },
        { text: 'Connect relationships', action: null },
        { text: 'Explore family history', action: null }
      ],
      color: '#bfa97a'
    },
    {
      id: 'privacy-folders',
      title: 'Privacy Folders',
      description: 'Secure document storage with advanced privacy controls',
      icon: 'privacy',
      steps: [
        { text: 'Click "Privacy Folders" in navigation', action: () => navigate(`/privacy-folders/${currentMember._id}`) },
        { text: 'Upload sensitive documents', action: null },
        { text: 'Set individual folder passcodes', action: null },
        { text: 'Organize by category or importance', action: null },
        { text: 'Access with secure authentication', action: null }
      ],
      color: '#d4af37'
    },
    {
      id: 'sharing',
      title: 'Share Documents',
      description: 'Securely share files with family members',
      icon: 'share',
      steps: [
        { text: 'Go to Privacy Folders', action: () => navigate(`/privacy-folders/${currentMember._id}`) },
        { text: 'Select a document to share', action: null },
        { text: 'Click the share button', action: null },
        { text: 'Choose recipient and permissions', action: null },
        { text: 'Send secure invitation', action: null }
      ],
      color: '#9b59b6'
    },
    {
      id: 'emergency-access',
      title: 'Emergency Access',
      description: 'Set up emergency access for trusted contacts',
      icon: 'emergency',
      steps: [
        { text: 'Click "Emergency Access" card', action: () => navigate('/emergency-access') },
        { text: 'Add trusted emergency contact', action: null },
        { text: 'Set up access permissions', action: null },
        { text: 'Create emergency code', action: null },
        { text: 'Test emergency access', action: null }
      ],
      color: '#ff6b6b'
    },
    {
      id: 'gallery',
      title: 'Memory Gallery',
      description: 'Browse and organize your family memories',
      icon: 'gallery',
      steps: [
        { text: 'Click "Gallery" in navigation', action: () => navigate(`/gallery/${currentMember._id}`) },
        { text: 'Browse your memories', action: null },
        { text: 'Filter by date or type', action: null },
        { text: 'View full-size images', action: null },
        { text: 'Download or share memories', action: null }
      ],
      color: '#4ecdc4'
    },
    {
      id: 'family-continuity-structure',
      title: 'Account Management',
      description: 'Manage family members and account settings',
      icon: 'users',
      steps: [
        { text: 'Click "Account Management" in navigation', action: () => navigate('/family-continuity-structure') },
        { text: 'View family members', action: null },
        { text: 'Add new members', action: null },
        { text: 'Manage permissions', action: null },
        { text: 'Update account settings', action: null }
      ],
      color: '#95a5a6'
    }
  ];

  const currentFeature = features[currentStep];

  const nextStep = () => {
    if (currentStep < features.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const executeStepAction = (stepIndex) => {
    const step = currentFeature.steps[stepIndex];
    if (step.action) {
      step.action();
      onClose();
    }
  };

  useEffect(() => {
    if (currentFeature) {
      gsap.fromTo('.feature-card', 
        { opacity: 0, y: 20, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "power3.out" }
      );
    }
  }, [currentFeature]);

  return (
    <div className="feature-explorer-overlay">
      <div className="feature-explorer-container">
        <div className="feature-explorer-header">
          <h2>🚀 Explore Features</h2>
          <p>Learn how to use all EternaVaults features step by step</p>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="feature-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentStep + 1) / features.length) * 100}%` }}
            />
          </div>
          <span className="progress-text">{currentStep + 1} of {features.length}</span>
        </div>

        <div className={`feature-card ${isAnimating ? 'animating' : ''}`}>
          <div className="feature-icon" style={{ backgroundColor: currentFeature.color }}>
            <Icon name={currentFeature.icon} size={32} />
          </div>
          <h3>{currentFeature.title}</h3>
          <p className="feature-description">{currentFeature.description}</p>

          <div className="steps-container">
            <h4>How to use:</h4>
            <div className="steps-list">
              {currentFeature.steps.map((step, index) => (
                <div 
                  key={index} 
                  className={`step-item ${step.action ? 'clickable' : ''}`}
                  onClick={() => executeStepAction(index)}
                >
                  <div className="step-number">{index + 1}</div>
                  <div className="step-content">
                    <span className="step-text">{step.text}</span>
                    {step.action && <span className="step-action">Click to go →</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="feature-navigation">
          <button 
            className="nav-btn prev-btn" 
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            ← Previous
          </button>
          <button 
            className="nav-btn next-btn" 
            onClick={nextStep}
            disabled={currentStep === features.length - 1}
          >
            Next →
          </button>
        </div>

        <div className="feature-dots">
          {features.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentStep ? 'active' : ''}`}
              onClick={() => setCurrentStep(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeatureExplorer;
